import React, { useState, useLayoutEffect, useMemo, Suspense } from 'react';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import find from 'lodash/find';
import { sort } from '@utility/helperFunction';
import { ERROR_MESSAGE } from '@constants';
import { LABEL } from '@veriables';
import style from '../index.module.scss';
import Loader from '@components/common/Loader/newLoaderModal';
import { TouchableButton, TouchableDiv } from '@components/common/Touchable';
import plusSign from '@assets/images/plusSign.svg';
import { ReactComponent as DeleteIcon } from '@assets/images/delete.svg';

const DeleteSeatModal = React.lazy(() => import('../Modals/DeleteSeat'));
const AddSeatModal = React.lazy(() => import('../Modals/AddSeat'));

const ServerAppScreen = ({
  isDefaultMode,
  addInProcessItemsToCart,
  serverAppTheme,
  ...props
}) => {
  useLayoutEffect(() => {
    if (isEmpty(props.position)) {
      const position =
        props.check?.seats?.length > 0
          ? props.check?.seats[0]
          : { number: -1, position: -1 };
      props.setPosition(position);
    }
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSeatDelOpen, setIsSeatDelOpen] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState();

  const closeModal = () => setIsModalOpen(false);

  const submitSeatNumber = val => {
    const seatArray = [];
    if (Array.isArray(val)) {
      val.forEach(e => seatArray.push({ number: e, tableShareType: 0 }));
    } else {
      seatArray.push({ number: val, tableShareType: 0 });
    }
    const cb = () => {
      const dataToSend = {
        storeId: props.deviceInfo?.storeId,
        deviceId: props.deviceInfo?.deviceId,
        employeeId: Number(props.userInfo?.id),
        employeeCard: props.userInfo?.employeeCard,
        checkHeaderId: props.check?.id,
        seats: seatArray,
      };
      props
        .occupySeat(dataToSend)
        .then(response => {
          props.updateChecksAfterResponse(response);
          const seatsData = filter(response.seats, seat => seat.open);
          const positionReq = find(
            seatsData,
            seat => seat.number === Number(dataToSend.seats[0].number)
          );
          const seatReq = { ...response, seats: sort(response.seats, 'number') };
          props.setPosition(positionReq);
          props.updateSeatInfo(seatReq);
          closeModal();
        })
        .catch(err => props.setErrorPopUp({ res: err.message }));
    };
    addInProcessItemsToCart(cb);
  };

  const handleSeatClick = item => props.setPosition(item);

  const handleClear = data => {
    if (props.cart.filter(e => e.seatIds.includes(data.id) && e.pending).length > 0) {
      props.setErrorPopUp({ res: ERROR_MESSAGE.SEND_PENDING_ITEMS });
    } else {
      const dataToSend = {
        storeId: props.deviceInfo?.storeId,
        employeeId: Number(props.userInfo?.id),
        employeeCard: props.userInfo?.employeeCard,
        deviceId: props.deviceInfo?.deviceId,
        checkHeaderId: props.check?.id,
        stationId: props.check?.stationId || null,
        seats: [{ seatId: data.id }],
      };
      props
        .seatClear(dataToSend)
        .then(res => {
          props.updateSeatInfo(res);
          props.setSeat(res);
          props.setPosition({ number: -1, position: -1 });
          setIsSeatDelOpen(false);
        })
        .catch(err => props.setErrorPopUp({ res: err.message }));
    }
  };

  const inlineStyles = useMemo(() => {
    const backgroundColor = { backgroundColor: serverAppTheme?.background_color };
    const tableButton = {
      backgroundColor:
        props?.position?.number === -1
          ? serverAppTheme?.primaryBtn
          : props?.cart?.length > 0
          ? serverAppTheme?.pending_items
          : serverAppTheme?.tip_tile,
      color: props?.position?.number === -1 ? 'white' : serverAppTheme?.primary_text,
    };
    const delButton = { backgroundColor: serverAppTheme?.primaryBtn };
    return { backgroundColor, tableButton, delButton };
  }, [serverAppTheme, props?.position, props?.cart.length]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', overflow: 'hidden', padding: 0 }}>
        {isDefaultMode && props.check?.id && (
          <>
            <div style={{ flexShrink: 0, paddingRight: '4px' }}>
              <TouchableDiv
                role="presentation"
                onClick={() => handleSeatClick({ number: -1, position: -1 })}
                className={cn(style.btnStyle, 'btn-ripple rounded color-white px-3 py-1 text-center fs_20')}
                style={{ ...inlineStyles.tableButton, display: 'flex' }}
                rippleEffect={true}
              >
                {LABEL().TABLE}
              </TouchableDiv>
            </div>

            <div style={{ flex: 1, minWidth: 0, overflowX: 'auto', display: 'flex', alignItems: 'center', backgroundColor: inlineStyles.backgroundColor.backgroundColor }}>
              {props.check?.seats?.length > 0 &&
                sort(props?.check?.seats, 'number').map(item => {
                  const hasPending = props.cart?.some(pending => pending.seatIds?.includes(item.id));
                  const isSelected = props.position.number === item.number;
                  const background = hasPending
                    ? isSelected ? serverAppTheme?.primaryBtn : '#F7E59E'
                    : isSelected ? serverAppTheme?.primaryBtn : serverAppTheme?.tip_tile;
                  const textColor = hasPending && !isSelected ? 'black' : isSelected ? 'white' : serverAppTheme?.primary_text;
                  const classes = cn(
                    style.btnBorderWhite,
                    style.btnStyle,
                    !isSelected ? style.btnGray : null,
                    isSelected ? style.selectedSeatBg : '',
                    hasPending && !isSelected ? style.pendingSeatBg : '',
                    'btn-ripple text-center py-1 rounded fs_20 position-relative'
                  );
                  return (
                    <div key={item.id} style={{ flexShrink: 0, paddingRight: '4px' }}>
                      {item.open && (
                        <TouchableDiv
                          role="presentation"
                          onClick={() => handleSeatClick(item)}
                          className={classes}
                          style={{ background, color: textColor, display: 'flex' }}
                          rippleEffect={true}
                        >
                          <span>{item.number}</span>
                        </TouchableDiv>
                      )}
                    </div>
                  );
                })}
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', paddingLeft: '4px', backgroundColor: inlineStyles.backgroundColor.backgroundColor }}>
              <div style={{ paddingRight: '4px' }}>
                <TouchableButton
                  type="button"
                  className={cn(style.btnAddStyle, 'btn-ripple')}
                  style={{ borderRadius: '5px', display: 'flex' }}
                  onClick={e => { e.preventDefault(); setIsModalOpen(true); }}
                  aria-label="plusBtn"
                  rippleEffect={true}
                >
                  <img role="presentation" src={plusSign} alt="plus" style={{ width: '2.777rem' }} />
                </TouchableButton>
              </div>
              <TouchableButton
                type="button"
                onClick={e => { e.preventDefault(); setIsSeatDelOpen(!isSeatDelOpen); }}
                className={cn(style.btnDelStyle, 'btn-ripple color-white')}
                style={{ ...inlineStyles.delButton, borderRadius: '5px', display: 'flex' }}
                rippleEffect={true}
              >
                <DeleteIcon fill="white" />
              </TouchableButton>
            </div>
          </>
        )}
      </div>

      <Suspense fallback={<Loader />}>
        {isSeatDelOpen && (
          <DeleteSeatModal
            check={props.check}
            isSeatDelOpen={isSeatDelOpen}
            setIsSeatDelOpen={setIsSeatDelOpen}
            selectedSeat={selectedSeat}
            setSelectedSeat={setSelectedSeat}
            serverAppTheme={serverAppTheme}
            isLandScape={props.isLandScape}
            handleClear={handleClear}
            setErrorPopUp={props.setErrorPopUp}
            deviceInfo={props?.deviceInfo}
          />
        )}
      </Suspense>

      <Suspense fallback={<Loader />}>
        {isModalOpen && (
          <AddSeatModal
            check={props.check}
            loading={props.loading}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            submitSeatNumber={submitSeatNumber}
            serverAppTheme={serverAppTheme}
            isLandScape={props.isLandScape}
            setErrorPopUp={props.setErrorPopUp}
          />
        )}
      </Suspense>
    </>
  );
};

export default ServerAppScreen;
